import { cn } from '@goaltree/ui/lib/utils'
import { Loader2, ThumbsDown } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { LoadingFacts } from '@/components/loading-facts'

export const CRITIQUE_KEYS = [
  'too_aggressive',
  'too_vague',
  'missing_point',
  'wrong_order',
] as const
export type CritiqueKey = (typeof CRITIQUE_KEYS)[number]

const CRITIQUE_LABEL: Record<CritiqueKey, string> = {
  too_aggressive: 'secondOpinion.tooAggressive',
  too_vague: 'secondOpinion.tooVague',
  missing_point: 'secondOpinion.missingPoint',
  wrong_order: 'secondOpinion.wrongOrder',
}

// "Second Opinion": lets the user say what's wrong with an AI plan and regenerate
// a revised one, instead of silently discarding and retyping the goal.
export function SecondOpinion({
  onSubmit,
  loading = false,
  className,
}: {
  onSubmit: (critique: CritiqueKey[], note: string) => void
  loading?: boolean
  className?: string
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [selected, setSelected] = useState<CritiqueKey[]>([])
  const [note, setNote] = useState('')
  // `loading` is shared with saving; facts only show for a revision this panel started.
  const [submitted, setSubmitted] = useState(false)

  useEffect(() => {
    if (!loading) setSubmitted(false)
  }, [loading])

  const toggle = (k: CritiqueKey) =>
    setSelected((prev) => (prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]))

  const canSubmit = (selected.length > 0 || note.trim().length > 0) && !loading

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={cn(
          'flex items-center gap-1.5 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground',
          className,
        )}
      >
        <ThumbsDown className="h-3.5 w-3.5" />
        {t('secondOpinion.trigger')}
      </button>
    )
  }

  return (
    <div
      className={cn(
        'w-full basis-full rounded-2xl border border-border/60 bg-muted/20 p-3',
        className,
      )}
    >
      <p className="mb-2 text-xs font-semibold text-muted-foreground">
        {t('secondOpinion.prompt')}
      </p>
      <div className="flex flex-wrap gap-1.5">
        {CRITIQUE_KEYS.map((k) => (
          <button
            key={k}
            type="button"
            onClick={() => toggle(k)}
            className={cn(
              'cursor-pointer rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
              selected.includes(k)
                ? 'border-primary/50 bg-primary/15 text-primary-text'
                : 'border-border/60 bg-background text-muted-foreground hover:bg-muted',
            )}
          >
            {t(CRITIQUE_LABEL[k])}
          </button>
        ))}
      </div>
      <input
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder={t('secondOpinion.notePlaceholder')}
        maxLength={200}
        className="mt-2 w-full rounded-lg border border-border/60 bg-background px-2.5 py-1.5 text-xs outline-none focus:border-primary"
      />
      {loading && submitted && <LoadingFacts className="mt-3 max-w-none bg-background/60" />}
      <div className="mt-2 flex justify-end gap-2">
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="cursor-pointer rounded-lg px-2.5 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted"
        >
          {t('common.cancel')}
        </button>
        <button
          type="button"
          disabled={!canSubmit}
          onClick={() => {
            setSubmitted(true)
            onSubmit(selected, note.trim())
          }}
          className="flex cursor-pointer items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-xs font-bold text-white transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50 dark:text-[#0a0a0a]"
        >
          {loading && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          {loading ? t('secondOpinion.regenerating') : t('secondOpinion.submit')}
        </button>
      </div>
    </div>
  )
}
